import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import type { Session } from '@aurora/contracts';
import { ApiError, get, post } from './api';
import { hardNavigation } from './hard-navigation';

/**
 * Session state.
 *
 * The session is a query like any other, which means it participates in the
 * cache and refetches on window focus without a bespoke state machine.
 * Permissions arrive with it so the interface can hide actions the user cannot
 * take — a courtesy, never a control, since every one of them is enforced
 * again on the server.
 *
 * "Signed out" is a value — `null` — and not an error. It used to be the
 * error a 401 left behind, and React Query keeps the last good data when a
 * refetch fails: so a session that expired overnight, was revoked from another
 * browser, or was ended by signing out, went on looking signed in. Every page
 * failed with "Authentication required" and nothing ever went back to the
 * sign-in page. Now the answer to "who is signed in?" can be "nobody", and the
 * guards act on it.
 */

interface SessionContextValue {
  session: Session | null;
  isLoading: boolean;
  /**
   * True while a request for the session is in flight, including refetches.
   *
   * Separate from `isLoading`, which React Query defines as "pending and
   * fetching" — false for any background refetch. The guard on protected pages
   * needs to tell "we are checking" from "we checked, they are out", or it
   * bounces people to the sign-in page while the answer is still on its way.
   */
  isFetching: boolean;
  /** A failure to *ask* — the network, the server — never "not signed in". */
  error: ApiError | null;
  refetch: () => void;
  /**
   * Ends the session on the server and forgets it here.
   *
   * Rejects, and leaves the person signed in, if the server could not be told:
   * the session cookie is out of this page's reach, so pretending would leave
   * a live session behind on what may be a shared machine.
   */
  signOut: () => Promise<void>;
  /**
   * Forgets a session the server has already ended — a password change ends
   * every session, including the one that made it — and says where to go.
   */
  endSession: (next?: string) => void;
  /**
   * Hands an impersonated session back to the administrator who started it,
   * with a full page load so nothing cached for the other person survives.
   */
  stopImpersonating: () => Promise<void>;
  /**
   * Where a session that was ended on purpose should land, while there is no
   * session. Null when the session simply lapsed — then the sign-in page should
   * bring the person back to the page they were on, and a deliberate sign-out
   * should not.
   */
  sessionEndedTo: string | null;
  /** True when the actor holds the permission, in the given workspace if named. */
  can: (permission: string, workspaceId?: number) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const sessionQueryKey = ['session'] as const;

/**
 * Asks the server who is signed in.
 *
 * A 401 from `/auth/me` is an answer — nobody, or a sign-in still owing a code
 * or a new password — so it resolves to `null`. Anything else is a failure to
 * ask, and stays an error.
 */
export async function fetchSession(): Promise<Session | null> {
  try {
    return await get<Session>('/auth/me');
  } catch (error) {
    if (error instanceof ApiError && error.isAuthError) return null;
    throw error;
  }
}

/**
 * Loads the session into the cache and waits for it.
 *
 * Always a fresh request. `fetchQuery` joins a check already on its way — the
 * one a window regaining focus starts, say, on returning from a password
 * manager — and that one was sent before the sign-in set a cookie, so it can
 * only answer "nobody". Cancelling it first means the answer is about the
 * session that now exists.
 *
 * A failure resolves to `null` rather than throwing, so a sign-in never turns
 * into a stack trace; `openSession` says what the `null` meant.
 */
export async function loadSessionIntoCache(queryClient: QueryClient): Promise<Session | null> {
  try {
    await queryClient.cancelQueries({ queryKey: sessionQueryKey });
    return await queryClient.fetchQuery({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
      staleTime: 0,
    });
  } catch {
    return null;
  }
}

/** Why a sign-in the server accepted left no session in this browser. */
export interface SessionNotOpened {
  /**
   * `not_kept`: asked who is signed in, the server said nobody — the browser
   * did not keep, or did not send back, the cookie it had just been given
   * (cookies blocked, or an address the desk was not set up for).
   * `failed`: the question itself failed, and may well work a moment later.
   */
  reason: 'not_kept' | 'failed';
  /** A request id, when the server itself failed. */
  reference?: string;
}

/**
 * Opens the session a sign-in step has just created, or says why there is
 * none. Resolves to `null` when the session is open.
 *
 * Deliberately does not navigate. `RedirectIfSignedIn`, around every sign-in
 * page, moves the person on to where they were going once the session reaches
 * the provider — and that is a moment *after* this resolves: React Query tells
 * a query's watchers on a timer, not in the same tick. A sign-in handler that
 * also navigated always got there first, so the page opened while its own
 * guard still saw nobody, sent the person back to sign in, and opened again
 * when the session caught up.
 */
export async function openSession(queryClient: QueryClient): Promise<SessionNotOpened | null> {
  const session = await loadSessionIntoCache(queryClient);
  if (session) return null;

  const failure = queryClient.getQueryState<Session | null, unknown>(sessionQueryKey)?.error ?? null;
  if (!failure) return { reason: 'not_kept' };
  return {
    reason: 'failed',
    ...(failure instanceof ApiError && failure.isServerFault && failure.requestId
      ? { reference: failure.requestId }
      : {}),
  };
}

/** A 401 that means the session a request relied on is gone. */
function endsSession(error: unknown): boolean {
  return error instanceof ApiError && error.endsSession;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [, rerender] = useReducer((count: number) => count + 1, 0);

  /**
   * Set just before a deliberate end clears the cached session, and read by
   * the guard when it sees the session gone. A ref, not state, on purpose:
   * the guard has to see it in the very render where the session first reads
   * as null, and a state update would land in a render of its own — after the
   * guard had already sent the person to sign in with a "come back to" link
   * to a page they had just chosen to leave.
   */
  const endedTo = useRef<string | null>(null);

  const query = useQuery<Session | null, ApiError>({
    queryKey: sessionQueryKey,
    queryFn: fetchSession,
    // A 4xx will be refused again identically; a dropped connection or a
    // restarting server is worth another try before saying so.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failureCount < 2,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const session = query.data ?? null;

  // A session again: whatever ended the last one no longer applies.
  useEffect(() => {
    if (session) endedTo.current = null;
  }, [session]);

  useEffect(() => {
    /**
     * The global 401 path.
     *
     * Any query or mutation that is refused because the session is gone —
     * expired, revoked, signed out in another tab — has the session checked
     * again. If it really is over, the check answers `null` and the guard
     * takes the person to sign in, carrying the page they were on so they come
     * back to it. A wrong password or code is also a 401, but a different one
     * (`invalid_credentials`), and is left alone.
     *
     * `cancelRefetch: false`, because a page that loads ten things gets ten
     * refusals at once, and each one cancelling the check the last one started
     * would turn one request into ten.
     */
    const recheck = () =>
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey }, { cancelRefetch: false });

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] === sessionQueryKey[0]) {
        /**
         * Somebody cleared the cache — `queryClient.clear()`, or
         * `removeQueries` on the session. React Query destroys the query
         * without telling its observers, so this provider would keep
         * rendering the identity it last saw, forever. Re-rendering builds a
         * fresh query for the observer to follow, and that fetches.
         */
        if (event.type === 'removed') rerender();
        return;
      }
      if (event.type === 'updated' && event.action.type === 'error' && endsSession(event.action.error)) {
        recheck();
      }
    });

    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error' && endsSession(event.action.error)) {
        recheck();
      }
    });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient]);

  const endSession = useCallback(
    (next = '/login') => {
      endedTo.current = next;
      // Everything another identity's screens loaded goes; the session entry
      // itself stays, set to "nobody", so this provider is told. Clearing the
      // whole cache instead is what left the signed-in screen up after
      // "Sign out": it destroys the query without notifying anyone watching it.
      queryClient.removeQueries({ predicate: (cached) => cached.queryKey[0] !== sessionQueryKey[0] });
      queryClient.setQueryData<Session | null>(sessionQueryKey, null);
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await post('/auth/logout');
    } catch (error) {
      // Already over on the server, which is the outcome being asked for.
      if (!(error instanceof ApiError && error.isAuthError)) throw error;
    }
    endSession('/login');
  }, [endSession]);

  const stopImpersonating = useCallback(async () => {
    await post('/auth/stop-impersonating');
    // A full load, not a navigation: the cache, the open screens and their
    // half-typed state all belong to the person being impersonated.
    hardNavigation.assign('/admin/people');
  }, []);

  const can = useCallback(
    (permission: string, workspaceId?: number) => {
      if (!session) return false;
      return session.permissions.some(
        (grant) =>
          grant.permission === permission &&
          (workspaceId === undefined ||
            grant.workspaceId === null ||
            grant.workspaceId === workspaceId),
      );
    },
    [session],
  );

  // Read during render on purpose — see `endedTo` above.
  const sessionEndedTo = session ? null : endedTo.current;

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      error: query.error instanceof ApiError ? query.error : null,
      refetch: () => void query.refetch(),
      signOut,
      endSession,
      stopImpersonating,
      sessionEndedTo,
      can,
    }),
    [
      session,
      query.isLoading,
      query.isFetching,
      query.error,
      query.refetch,
      signOut,
      endSession,
      stopImpersonating,
      sessionEndedTo,
      can,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}

/**
 * The signed-in user, or a thrown error.
 *
 * For use below the authentication boundary, where a session is guaranteed and
 * null-checking every access would be noise.
 */
export function useCurrentUser(): Session {
  const { session } = useSession();
  if (!session) throw new Error('useCurrentUser used outside an authenticated route');
  return session;
}
